/**
 * Decision latency service — the fetch half of the L1 read model.
 *
 * PRD: docs/9-exploration/DECISION-LATENCY-INSTRUMENTATION-PRD.md.
 * Arithmetic, vocabulary and null-honesty: src/lib/decisionLatencyCore.ts.
 *
 * READ-ONLY. This module issues SELECTs and nothing else — no RPCs, no
 * inserts, no updates, no new tables, no new timestamps. Every stamp it reads
 * already existed; the PRD's whole claim is that the pipeline was instrumented
 * all along and nobody ever did the subtraction.
 *
 * Reads go through the typed client so a renamed or dropped stamp column is a
 * compile error rather than a silently empty report — which for this feature is
 * the difference between "that stage is fast" and "that stage is gone".
 *
 * SHAPE OF THE CHAIN. The catchball loop is the spine, because it is the only
 * row that carries the ask itself (`request_issued_at`):
 *
 *     catchball_loops ──loop_id──→ catchball_proposals
 *            │                            │
 *            │                       session_id (mig 670 provenance)
 *            │                            ↓
 *            │                   prioritization_sessions
 *            └────────cycle_id───────→ cycle_reports
 *
 * The authorization stage would continue `cycle_reports → POR snapshot`, but
 * that join does not exist: migration 678 left `source_cycle_report_id` NULL on
 * purpose ("wiring it is a follow-up, not silently guessed here"). So this
 * service does not populate `porAuthorizedAt`, and the core reports that one
 * segment as unmeasured with the reason attached. It is degraded by
 * construction, exactly as the PRD says, and it says so on the page rather than
 * rendering a suspiciously fast final stage.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import {
  aggregateLatency,
  assembleLatencyChains,
  type LatencyAggregate,
  type LatencyChain,
} from './decisionLatencyCore';

export interface LatencyQueryOptions {
  /** Restrict to one catchball cycle. Omit for the whole workspace. */
  cycleId?: string;
  /** Only loops issued on or after this ISO stamp. */
  since?: string;
  /** Safety bound on the spine query. */
  limit?: number;
}

export interface LatencyReport {
  chains: LatencyChain[];
  aggregate: LatencyAggregate;
  error?: string;
}

const DEFAULT_LIMIT = 200;

const EMPTY_REPORT: LatencyReport = {
  chains: [],
  aggregate: aggregateLatency([]),
};

/**
 * Assembles latency chains for a workspace.
 *
 * Four narrow SELECTs rather than one nested join: PostgREST embedding across
 * four tables with two different foreign keys produces a query that is harder
 * to read than the arithmetic it feeds, and the row counts here are small
 * (loops per workspace, not events). Correctness and legibility win.
 *
 * A failure on any leg returns the error rather than a partial report — a
 * latency report missing a whole table would read as "that stage is unmeasured"
 * across the board, which is precisely the lie this feature exists to prevent.
 */
export async function fetchLatencyReport(
  workspaceId: string,
  options: LatencyQueryOptions = {},
): Promise<LatencyReport> {
  const db = getTypedSupabaseClient();
  const limit = options.limit ?? DEFAULT_LIMIT;

  let loopQuery = db
    .from('catchball_loops')
    .select('id, cycle_id, request_issued_at, ball_claimed_at, proposal_submitted_at')
    .eq('workspace_id', workspaceId)
    .order('request_issued_at', { ascending: false, nullsFirst: false })
    .limit(limit);

  if (options.cycleId) loopQuery = loopQuery.eq('cycle_id', options.cycleId);
  if (options.since) loopQuery = loopQuery.gte('request_issued_at', options.since);

  const { data: loops, error: loopError } = await loopQuery;
  if (loopError) return { ...EMPTY_REPORT, error: loopError.message };
  if (!loops || loops.length === 0) return EMPTY_REPORT;

  const loopIds = loops.map((l) => l.id);
  const cycleIds = Array.from(
    new Set(loops.map((l) => l.cycle_id).filter((id): id is string => Boolean(id))),
  );

  const [proposalsRes, reportsRes] = await Promise.all([
    db
      .from('catchball_proposals')
      .select('id, loop_id, session_id, packaged_at, proposed_at, responded_at, resolved_at')
      .in('loop_id', loopIds),
    cycleIds.length > 0
      ? db
          .from('cycle_reports')
          .select('id, cycle_id, created_at, ratified_at')
          .in('cycle_id', cycleIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (proposalsRes.error) return { ...EMPTY_REPORT, error: proposalsRes.error.message };
  if (reportsRes.error) return { ...EMPTY_REPORT, error: reportsRes.error.message };

  const proposals = proposalsRes.data ?? [];
  const reports = reportsRes.data ?? [];

  const sessionIds = Array.from(
    new Set(proposals.map((p) => p.session_id).filter((id): id is string => Boolean(id))),
  );

  const { data: sessions, error: sessionError } =
    sessionIds.length > 0
      ? await db
          .from('prioritization_sessions')
          .select('id, created_at, criteria_finalized_at, run_completed_at')
          .in('id', sessionIds)
      : { data: [], error: null };

  if (sessionError) return { ...EMPTY_REPORT, error: sessionError.message };

  const chains = assembleLatencyChains({ loops, proposals, reports, sessions: sessions ?? [] });

  return { chains, aggregate: aggregateLatency(chains) };
}
